import {useSyncExternalStore} from 'react';
import {PasswordCredentialsSchema} from '../../src/auth-policy';
import {PRODUCT_ENTRY_LABEL} from '../../src/product';
import {NativeTokenReconnectSchema, NativeTokenSnapshotSchema} from '../../src/native-hermes-types';
import {AccountCreditBalanceSchema, type AccountCreditBalance} from '../../src/account-credit-types';
import {EventStreamDecoder} from '../../src/event-stream';
import {GOOGLE_ERROR_FRAGMENT, GoogleCallbackErrorSchema, GoogleStatusSchema, type GoogleService} from '../../src/google-types';
import type {AccountDocument, AccountSettings, AccountProfile, WhatsAppStatus, AppConfig, ErrorDetails, Health, Resource,
  AccountApplications, ApplicationRegistration, ApplicationStatus, ApplicationLogs, GoogleStatus, AuthMode,
  Session, HermesState, NativeRuntimeStatus, NativeSessionMapping, NativeSessionDetail, NativeAgentDetail,
  NativeEventBatch, NativeMessageResult, NativeTerminalTicket, NativeTokenSnapshot} from './types';

const idle = <T>(): Resource<T> => ({status: 'idle', data: null, error: null});
const ready = <T>(data: T): Resource<T> => ({status: 'ready', data, error: null});
const nativeResources = () => ({nativeDetail: idle<NativeSessionDetail>(), nativeAgent: idle<NativeAgentDetail>(),
  nativeEvents: idle<NativeEventBatch>(), nativeMessageResult: idle<NativeMessageResult>(), nativeTokens: idle<NativeTokenSnapshot>()});
let state: HermesState = {config: idle<AppConfig>(), health: idle<Health>(), session: idle<Session | null>(),
  accountSettings: idle<AccountSettings>(), accountProfile: idle<AccountProfile>(), accountBalance: idle<AccountCreditBalance>(), whatsapp: idle<WhatsAppStatus>(), google: idle<GoogleStatus>(),
  accountApplications: idle<AccountApplications>(), applicationLogs: idle<ApplicationLogs>(), applicationLogSlug: null,
  nativeRuntime: idle<NativeRuntimeStatus>(), nativeSessions: idle<NativeSessionMapping[]>(), ...nativeResources(),
  nativeTarget: null, nativeSelectedAgentId: null, nativeConnection: {status: 'closed', error: null},
  walletAddress: null, operation: {label: null, error: null, scope: null}};
const listeners = new Set<() => void>();
const publish = (patch: Partial<HermesState>): void => {state = {...state, ...patch}; for (const listener of listeners) listener();};
let authGeneration = 0;
let sessionLoadGeneration = 0;
let accountSettingsGeneration = 0;
let accountProfileGeneration = 0;
let accountBalanceGeneration = 0;
let accountRefresh: Promise<void> | null = null;
let accountApplicationsGeneration = 0;
let applicationLogsGeneration = 0;
let whatsappGeneration = 0;
let googleGeneration = 0;
let nativeGeneration = 0;
let nativeRuntimeGeneration = 0;
let nativeSessionsGeneration = 0;
let nativeDetailGeneration = 0;
let nativeAgentGeneration = 0;
let nativePollController: AbortController | null = null;
let nativeTokenController: AbortController | null = null;
let nativeTokenGeneration = 0;
let initialLoad: Promise<void> | null = null;
let stopWalletWatch: (() => void) | null = null;

export class ApiError extends Error {
  constructor(public readonly details: ErrorDetails) {super(details.message); this.name = details.name;}
}
class TokenTransportError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message, cause === undefined ? undefined : {cause});
    this.name = 'TokenTransportError';
  }
}
function diagnosticValue(value: unknown, seen = new WeakSet<object>()): unknown {
  if (typeof value === 'bigint') return value.toString();
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return '[Circular error reference]';
  seen.add(value);
  if (Array.isArray(value)) return value.map(item => diagnosticValue(item, seen));
  const entries = value instanceof Error ? {...value, name: value.name, message: value.message,
    stack: value.stack, cause: value.cause} : value;
  return Object.fromEntries(Object.entries(entries).map(([key, item]) => [key, diagnosticValue(item, seen)]));
}
export function errorDetails(error: unknown): ErrorDetails {
  if (error instanceof ApiError) return error.details;
  if (error instanceof Error) return {name: error.name, message: error.message,
    ...(error.stack ? {stack: error.stack} : {}), ...(error.cause !== undefined ? {cause: diagnosticValue(error.cause)} : {})};
  return {name: 'Error', message: String(error)};
}
export function formatError(error: ErrorDetails | Error | null | undefined): string {
  if (!error) return '';
  const details = error instanceof Error ? errorDetails(error) : error;
  const cause = details.cause === undefined ? '' : `\nCause: ${typeof details.cause === 'string' ? details.cause : JSON.stringify(details.cause, null, 2)}`;
  return `${details.stack ?? `${details.name}: ${details.message}`}${cause}`;
}

function base(): string {
  const configured = state.config.data;
  const baseUrl = import.meta.env.BASE_URL;
  if (!baseUrl.startsWith('/') || !baseUrl.endsWith('/')) throw new Error('The build must declare an absolute application base path.');
  return configured ? `${configured.basePath}/api` : `${baseUrl}api`;
}
async function responseBody<T>(response: Response, permitHealthError = false): Promise<T> {
  const text = await response.text();
  if (response.status === 204) return undefined as T;
  let body: unknown;
  try {body = JSON.parse(text);}
  catch (cause) {throw new ApiError({name: 'InvalidApiResponse', message: `HTTP ${response.status}: ${text}`, status: response.status,
    cause: errorDetails(cause)});}
  if (!response.ok && !permitHealthError) {
    const details = typeof body === 'object' && body !== null && 'error' in body ? body.error : null;
    if (details && typeof details === 'object' && 'message' in details) {
      throw new ApiError({...details as ErrorDetails, status: response.status});
    }
    throw new ApiError({name: 'HttpError', message: `HTTP ${response.status}: ${text}`, status: response.status});
  }
  return body as T;
}
function rejectExpiredAccount(path: string, error: unknown, authenticated: Session | null, generation: number): void {
  const accountRequest = path === '/auth/logout' || path.startsWith('/account/') || path.startsWith('/hermes/');
  if (accountRequest && error instanceof ApiError && error.details.status === 401 && authenticated !== null &&
      generation === authGeneration && state.session.data?.csrfToken === authenticated.csrfToken) {
    acceptSession(null);
    // Account failures do not belong to a sign-in submission.
    publish({operation: {...state.operation, error: error.details, scope: 'account'}});
  }
}
export async function request<T>(path: string, options: {body?: unknown; csrf?: string; signal?: AbortSignal; health?: boolean; method?: 'GET' | 'POST' | 'PUT' | 'DELETE'} = {}): Promise<T> {
  const authenticated = state.session.data;
  const generation = authGeneration;
  const headers: Record<string, string> = {'Accept': 'application/json'};
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.csrf) headers['x-csrf-token'] = options.csrf;
  const method = options.method ?? (options.body === undefined ? 'GET' : 'POST');
  const url = `${base()}${path}`;
  let response: Response;
  try {
    response = await fetch(url, {method, credentials: 'same-origin',
      headers, ...(options.body === undefined ? {} : {body: JSON.stringify(options.body)}),
      ...(options.signal ? {signal: options.signal} : {})});
  } catch (error) {
    if (options.signal?.aborted) throw error;
    const details = errorDetails(error);
    throw new ApiError({name: 'NetworkError', message: `${method} ${url}: ${details.message}`,
      ...(details.stack ? {stack: details.stack} : {}), cause: details});
  }
  try {return await responseBody<T>(response, options.health === true && response.status === 503);}
  catch (error) {
    rejectExpiredAccount(path, error, authenticated, generation);
    throw error;
  }
}
type ResourceKey = {[K in keyof HermesState]: HermesState[K] extends Resource<unknown> ? K : never}[keyof HermesState];
type ResourceData<K extends ResourceKey> = HermesState[K] extends Resource<infer T> ? T : never;
async function query<K extends ResourceKey>(key: K, fetcher: () => Promise<ResourceData<K>>, valid = () => true): Promise<ResourceData<K>> {
  publish({[key]: {status: 'loading', data: state[key].data, error: null}});
  try {
    const data = await fetcher();
    if (valid()) publish({[key]: ready(data)});
    return data;
  } catch (error) {
    if (valid()) publish({[key]: {status: 'error', data: null, error: errorDetails(error)}});
    throw error;
  }
}
function config(): AppConfig {
  if (state.config.status !== 'ready' || !state.config.data) throw new Error('Load the service chain configuration before connecting a wallet.');
  return state.config.data;
}
export function session(): Session {
  if (state.session.status !== 'ready' || !state.session.data) throw new Error('Sign in to your account first.');
  return state.session.data;
}
async function operation<T>(label: string, callback: () => Promise<T>, scope: NonNullable<HermesState['operation']['scope']> = 'account'): Promise<T> {
  if (state.operation.label) throw new Error(`Wait for the current operation: ${state.operation.label}`);
  publish({operation: {label, error: null, scope}});
  try {return await callback();}
  catch (error) {publish({operation: {label: null, error: errorDetails(error), scope}}); throw error;}
  finally {if (state.operation.label === label) publish({operation: {...state.operation, label: null}});}
}
function clearAccount(): void {
  accountBalanceGeneration++;
  authGeneration++; accountSettingsGeneration++; accountProfileGeneration++; whatsappGeneration++; googleGeneration++; nativeRuntimeGeneration++; nativeSessionsGeneration++;
  accountApplicationsGeneration++; applicationLogsGeneration++;
  closeHermes();
  publish({accountSettings: idle<AccountSettings>(), accountProfile: idle<AccountProfile>(), accountBalance: idle<AccountCreditBalance>(), whatsapp: idle<WhatsAppStatus>(), google: idle<GoogleStatus>(),
    accountApplications: idle<AccountApplications>(), applicationLogs: idle<ApplicationLogs>(), applicationLogSlug: null,
    nativeRuntime: idle<NativeRuntimeStatus>(), nativeSessions: idle<NativeSessionMapping[]>(), ...nativeResources(),
    nativeTarget: null, nativeSelectedAgentId: null});
}
async function loadSession(): Promise<Session | null> {
  try {return await request<Session>('/auth/session');}
  catch (error) {if (error instanceof ApiError && error.details.status === 401) return null; throw error;}
}
function acceptSession(next: Session | null): void {
  sessionLoadGeneration++;
  if (next === null || next.authMethod !== 'wallet') {
    stopWalletWatch?.(); stopWalletWatch = null;
    publish({walletAddress: null});
  }
  const previous = state.session.data;
  if (next === null || previous?.accountId !== next.accountId || previous.address !== next.address ||
      previous.chainId !== next.chainId || previous.csrfToken !== next.csrfToken) clearAccount();
  publish({session: ready(next)});
}
async function refreshSession(): Promise<void> {
  const generation = ++sessionLoadGeneration;
  publish({session: {status: 'loading', data: state.session.data, error: null}});
  try {
    const authenticated = await loadSession();
    if (generation === sessionLoadGeneration) acceptSession(authenticated);
  } catch (error) {
    if (generation === sessionLoadGeneration) {
      clearAccount();
      publish({session: {status: 'error', data: null, error: errorDetails(error)}});
    }
    throw error;
  }
}
async function loadConfig(): Promise<AppConfig> {
  const result = await request<AppConfig>('/config');
  if (result.publicOrigin !== window.location.origin || `${result.basePath}/` !== import.meta.env.BASE_URL) {
    throw new Error('The browser origin or application path differs from the API configuration.');
  }
  return result;
}
async function refreshAccount(): Promise<void> {
  await Promise.allSettled([refreshAccountSettings(), refreshAccountProfile(), refreshAccountBalance(), refreshWhatsApp(), refreshHermesRuntime(), refreshNativeSessions()]);
}
async function refreshAccountBalance(): Promise<AccountCreditBalance> {
  session();
  const generation = authGeneration, balanceGeneration = ++accountBalanceGeneration;
  return query('accountBalance', async () => AccountCreditBalanceSchema.parse(await request('/account/balance')),
    () => generation === authGeneration && balanceGeneration === accountBalanceGeneration);
}
function refreshAccountOnFocus(): void {
  if (document.visibilityState !== 'visible' || !state.session.data || accountRefresh !== null) return;
  accountRefresh = refreshAccount().finally(() => {accountRefresh=null;});
}
async function authenticateWithPassword(path: '/auth/login' | '/auth/register' | '/auth/setup-login',
  email: string, password: string, token?: string): Promise<Session> {
  const credentials = PasswordCredentialsSchema.parse({email, password});
  const authenticated = await request<Session>(path, {body: {...credentials, ...(token === undefined ? {} : {token})}});
  acceptSession(authenticated);
  await refreshAccount();
  return authenticated;
}
async function refreshNativeSessions(): Promise<NativeSessionMapping[]> {
  session();
  const generation = authGeneration;
  const sessionsGeneration = ++nativeSessionsGeneration;
  return query('nativeSessions', async () => (await request<{sessions: NativeSessionMapping[]}>('/hermes/sessions')).sessions,
    () => generation === authGeneration && sessionsGeneration === nativeSessionsGeneration);
}
async function refreshAccountSettings(): Promise<AccountSettings> {
  session();
  const generation = authGeneration;
  const settingsGeneration = ++accountSettingsGeneration;
  return query('accountSettings', () => request<AccountSettings>('/account/settings'),
    () => generation === authGeneration && settingsGeneration === accountSettingsGeneration);
}
async function changeAccountSettings(path: string, method: 'PUT' | 'DELETE', body?: unknown): Promise<AccountSettings> {
  const generation = authGeneration;
  accountSettingsGeneration++;
  const settings = await request<AccountSettings>(path, {method, ...(body === undefined ? {} : {body}), csrf: session().csrfToken});
  if (generation !== authGeneration) throw new Error('The account changed before the settings were saved.');
  accountSettingsGeneration++;
  publish({accountSettings: ready(settings)});
  return settings;
}

async function refreshAccountProfile(): Promise<AccountProfile> {
  session();
  const generation = authGeneration;
  const profileGeneration = ++accountProfileGeneration;
  return query('accountProfile', () => request<AccountProfile>('/account/profile'),
    () => generation === authGeneration && profileGeneration === accountProfileGeneration);
}

async function changeAccountUsername(username: string, expectedUsername: string): Promise<AccountProfile> {
  const authenticated = session();
  const generation = authGeneration;
  accountProfileGeneration++;
  const profile = await request<AccountProfile>('/account/username',
    {method: 'PUT', body: {username, expectedUsername}, csrf: authenticated.csrfToken});
  if (generation !== authGeneration) throw new Error('The account changed before the username was saved.');
  accountProfileGeneration++;
  publish({accountProfile: ready(profile)});
  accountApplicationsGeneration++;
  publish({accountApplications: idle<AccountApplications>()});
  void refreshApplications().catch(() => undefined);
  return profile;
}

async function refreshApplications(): Promise<AccountApplications> {
  session();
  const generation = authGeneration;
  const applicationsGeneration = ++accountApplicationsGeneration;
  return query('accountApplications', () => request<AccountApplications>('/account/applications'),
    () => generation === authGeneration && applicationsGeneration === accountApplicationsGeneration);
}

async function changeApplication(path: string, body: unknown): Promise<ApplicationStatus> {
  const authenticated = session();
  const generation = authGeneration;
  accountApplicationsGeneration++;
  try {
    const saved = await request<ApplicationStatus>(path, {method: 'POST', body, csrf: authenticated.csrfToken});
    if (generation !== authGeneration) throw new Error('The account changed before the service was updated.');
    accountApplicationsGeneration++;
    const current = state.accountApplications.data;
    if (current) publish({accountApplications: ready({...current,
      applications: current.applications.some(item => item.slug === saved.slug)
        ? current.applications.map(item => item.slug === saved.slug ? saved : item)
        : [...current.applications, saved]})});
    return saved;
  } finally {
    if (generation === authGeneration) void refreshApplications().catch(() => undefined);
  }
}

async function readApplicationLogs(slug: string): Promise<ApplicationLogs> {
  session();
  const generation = authGeneration;
  const logsGeneration = ++applicationLogsGeneration;
  publish({applicationLogSlug: slug, applicationLogs: idle<ApplicationLogs>()});
  return query('applicationLogs', async () => {
    const logs = await request<ApplicationLogs>(`/account/applications/${encodeURIComponent(slug)}/logs`);
    if (logs.slug !== slug) throw new Error('The service returned logs for a different application.');
    return logs;
  }, () => generation === authGeneration && logsGeneration === applicationLogsGeneration);
}

async function refreshWhatsApp(): Promise<WhatsAppStatus> {
  session();
  const generation = authGeneration;
  const connectionGeneration = ++whatsappGeneration;
  return query('whatsapp', () => request<WhatsAppStatus>('/account/whatsapp'),
    () => generation === authGeneration && connectionGeneration === whatsappGeneration);
}

async function changeWhatsApp(path: string, method: 'POST' | 'DELETE', body: {sessionId?: string} = {}): Promise<WhatsAppStatus> {
  const authenticated = session();
  const generation = authGeneration;
  whatsappGeneration++;
  const status = await request<WhatsAppStatus>(path, {method, body, csrf: authenticated.csrfToken});
  if (generation !== authGeneration) throw new Error('The account changed before the WhatsApp connection was updated.');
  whatsappGeneration++;
  publish({whatsapp: ready(status)});
  return status;
}

async function abortableDelay(milliseconds: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return;
  await new Promise<void>(resolve => {
    const finish = () => {clearTimeout(timer); signal.removeEventListener('abort', finish); resolve();};
    const timer = setTimeout(finish, milliseconds);
    signal.addEventListener('abort', finish, {once: true});
  });
}

async function refreshGoogle(): Promise<GoogleStatus> {
  session();
  const generation = authGeneration;
  const readGeneration = ++googleGeneration;
  return query('google', async () => GoogleStatusSchema.parse(await request('/account/google')),
    () => generation === authGeneration && readGeneration === googleGeneration);
}
async function authorizeGoogle(path: string, body: unknown): Promise<void> {
  const generation = authGeneration;
  const result = await request<{authorizationUrl: string}>(path, {body,
    ...(path.startsWith('/account/') ? {csrf: session().csrfToken} : {})});
  if (generation !== authGeneration) throw new Error('The account changed before Google authorization opened.');
  const target = new URL(result.authorizationUrl);
  if (target.origin !== 'https://accounts.google.com' || target.pathname !== '/o/oauth2/v2/auth') {
    throw new Error('The Google authorization address is invalid.');
  }
  window.location.assign(target.href);
}

async function refreshHermesRuntime(): Promise<NativeRuntimeStatus> {
  session();
  const generation = authGeneration;
  const runtimeGeneration = ++nativeRuntimeGeneration;
  return query('nativeRuntime', () => request<NativeRuntimeStatus>('/hermes/runtime'),
    () => authGeneration === generation && nativeRuntimeGeneration === runtimeGeneration);
}
async function refreshStartedWorkspace(accountGeneration: number, conversationGeneration?: number): Promise<void> {
  const valid = () => accountGeneration === authGeneration &&
    (conversationGeneration === undefined || conversationGeneration === nativeGeneration);
  if (!valid()) return;
  // Account reads keep their own request generations so navigation cannot discard
  // the current account's files or replace a newer settings response.
  const results = await Promise.allSettled([refreshHermesRuntime(), refreshAccountSettings()]);
  if (!valid()) return;
  const failures = results.flatMap(result => result.status === 'rejected' ? [result.reason] : []);
  if (failures.length === 1) throw failures[0];
  if (failures.length > 1) throw new AggregateError(failures, 'The workspace status and files could not be refreshed.',
    {cause: failures.map(errorDetails)});
}
function closeHermes(): void {
  nativeGeneration++; nativeDetailGeneration++; nativeAgentGeneration++;
  nativePollController?.abort(); nativePollController = null;
  nativeTokenController?.abort(); nativeTokenController = null;
  nativeTokenGeneration++;
  publish({...nativeResources(), nativeSelectedAgentId: null, nativeConnection: {status: 'closed', error: null}});
}
async function readNativeAgent(sessionId: string, agentId: string): Promise<NativeAgentDetail> {
  const detail = await request<NativeAgentDetail>(`/hermes/sessions/${encodeURIComponent(sessionId)}/agents/${encodeURIComponent(agentId)}`,
    {signal: AbortSignal.timeout(20_000)});
  if (detail.agent.sessionId !== agentId) throw new Error('The service returned a different agent conversation.');
  return detail;
}
async function refreshNativeDetail(sessionId: string, generation: number): Promise<NativeSessionDetail | null> {
  if (generation !== nativeGeneration) return null;
  const detailGeneration = ++nativeDetailGeneration;
  const requests: Promise<unknown>[] = [query('nativeDetail', async () => {
    const detail = await request<NativeSessionDetail>(`/hermes/sessions/${encodeURIComponent(sessionId)}`, {signal: AbortSignal.timeout(20_000)});
    if (detail.session.sessionId !== sessionId || detail.session.runId !== null) throw new Error('The service returned a different work chat.');
    if (!Number.isFinite(Date.parse(detail.observedAt)) || !Array.isArray(detail.openRequests)) throw new Error('The service returned an incomplete workspace snapshot.');
    return detail;
  },
    () => nativeGeneration === generation && nativeDetailGeneration === detailGeneration)];
  const agentId = state.nativeSelectedAgentId;
  if (agentId) {
    const agentGeneration = ++nativeAgentGeneration;
    requests.push(query('nativeAgent', () => readNativeAgent(sessionId, agentId),
      () => nativeGeneration === generation && nativeAgentGeneration === agentGeneration && state.nativeSelectedAgentId === agentId));
  }
  const results = await Promise.allSettled(requests);
  if (nativeGeneration !== generation) return null;
  if (results[0]?.status === 'rejected' && nativeDetailGeneration === detailGeneration) throw results[0].reason;
  // The selected transcript has its own error surface. It must not stop the team
  // snapshot, which owns live status, tasks, questions and usage for every actor.
  return nativeDetailGeneration === detailGeneration ? state.nativeDetail.data : null;
}
function pollHermes(sessionId: string, generation: number): void {
  const controller = new AbortController();
  nativePollController = controller;
  const accountGeneration = authGeneration;
  const valid = () => !controller.signal.aborted && generation === nativeGeneration && accountGeneration === authGeneration;
  void (async () => {
    let cursor = 0;
    let epoch: string | null = null;
    let count = 0;
    let runtimeRefreshedAt = Date.now();
    let recentEvents: Record<string, unknown>[] = [];
    while (valid()) {
      const parameters = new URLSearchParams({after: String(cursor)});
      if (epoch !== null) parameters.set('epoch', epoch);
      let batch: NativeEventBatch | null = null;
      for (let attempt=0; batch === null && valid(); attempt++) {
        try {
          batch = await request<NativeEventBatch>(`/hermes/sessions/${encodeURIComponent(sessionId)}/events?${parameters}`,
            {signal: AbortSignal.any([controller.signal, AbortSignal.timeout(20_000)])});
        } catch (error) {
          if (!valid()) return;
          if (attempt >= 4 || !(error instanceof ApiError && [502,503,504].includes(error.details.status ?? 0))) throw error;
          // Retry the same read cursor after a brief API restart. No input is resent.
          await abortableDelay(Math.min(4000,500*2**attempt),controller.signal);
        }
      }
      if (batch === null) return;
      if (!valid()) return;
      if (batch.resetRequired || (epoch !== null && epoch !== batch.epoch)) recentEvents = [];
      recentEvents = [...recentEvents, ...batch.events].slice(-200);
      cursor = batch.cursor; epoch = batch.epoch;
      publish({nativeEvents: ready({...batch, events: recentEvents})});
      const stateEvents = batch.events.some(event => event.type !== 'swarm.tokens' && event.type !== 'session.usage');
      if (count++ % 5 === 0 || stateEvents || batch.resetRequired) {
        await refreshNativeDetail(sessionId, generation);
      }
      if (!valid()) return;
      if (document.visibilityState === 'visible' && Date.now() - runtimeRefreshedAt >= 30_000) {
        const refreshed = await Promise.allSettled([refreshHermesRuntime(), refreshNativeSessions()]);
        if (refreshed[0].status === 'rejected') throw refreshed[0].reason;
        // A chat-list failure remains visible in its sidebar resource. It does not
        // replace the active conversation or stop its events.
        runtimeRefreshedAt = Date.now();
        if (!valid()) return;
      }
      publish({nativeConnection: {status: 'open', error: null}});
      await abortableDelay(1200, controller.signal);
    }
  })().catch(error => {
    if (valid()) publish({nativeConnection: {status: 'error', error: errorDetails(error)}});
  });
}
function streamNativeTokens(sessionId: string, generation: number): void {
  nativeTokenController?.abort();
  const tokenGeneration = ++nativeTokenGeneration;
  const controller = new AbortController();
  nativeTokenController = controller;
  const accountGeneration = authGeneration;
  const authenticated = state.session.data;
  const path = `/hermes/sessions/${encodeURIComponent(sessionId)}/tokens?credits=1`;
  const valid = () => !controller.signal.aborted && generation === nativeGeneration && accountGeneration === authGeneration && tokenGeneration === nativeTokenGeneration;
  publish({nativeTokens: {status: 'loading', data: null, error: null}});
  void (async () => {
    let failures = 0;
    while (valid()) {
      const attempt = new AbortController();
      const stopAttempt = () => attempt.abort(controller.signal.reason);
      controller.signal.addEventListener('abort', stopAttempt, {once: true});
      let deadline: ReturnType<typeof setTimeout> | null = null;
      const armDeadline = () => {
        if (deadline) clearTimeout(deadline);
        deadline = setTimeout(() => attempt.abort(new TokenTransportError('The token stream stopped responding.')), 30_000);
      };
      let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
      let receivedAt: number | null = null;
      let interrupted: unknown;
      let plannedRestart = false;
      try {
        armDeadline();
        let response: Response;
        try {
          response = await fetch(`${base()}${path}`,
            {credentials: 'same-origin', headers: {accept: 'text/event-stream'}, signal: attempt.signal});
        } catch (error) {
          throw attempt.signal.aborted ? attempt.signal.reason : new TokenTransportError('The token connection could not open.', error);
        }
        if (!response.ok) {
          try {await responseBody(response);}
          catch (error) {
            if ([502, 503, 504].includes(response.status)) throw new TokenTransportError('The token service is not available.', errorDetails(error));
            throw error;
          }
        }
        if (!response.headers.get('content-type')?.startsWith('text/event-stream') || !response.body) {
          throw new Error('The token connection did not return an event stream.');
        }
        reader = response.body.getReader();
        const decoder = new EventStreamDecoder();
        while (valid()) {
          let next: ReadableStreamReadResult<Uint8Array>;
          try {next = await reader.read();}
          catch (error) {
            throw attempt.signal.aborted ? attempt.signal.reason : new TokenTransportError('The token connection was interrupted.', error);
          }
          if (next.done) throw new TokenTransportError('The token connection closed.');
          armDeadline();
          for (const event of decoder.push(next.value)) {
            const value: unknown = JSON.parse(event.data);
            if (event.event === 'reconnect') {
              NativeTokenReconnectSchema.parse(value);
              plannedRestart = true;
              throw new TokenTransportError('The token service is restarting.');
            }
            if (event.event === 'error') {
              if (!value || typeof value !== 'object' || !('message' in value) || typeof value.message !== 'string') {
                throw new Error(`Invalid token stream error: ${event.data}`);
              }
              throw new ApiError(value as ErrorDetails);
            }
            if (event.event === 'credits' || event.event === 'credits-error') {
              if (!valid()) return;
              accountBalanceGeneration++;
              if (event.event === 'credits') publish({accountBalance: ready(AccountCreditBalanceSchema.parse(value))});
              else {
                if (!value || typeof value !== 'object' || !('message' in value)) throw new Error('Invalid credit stream error.');
                publish({accountBalance: {status: 'error', data: null, error: value as ErrorDetails}});
              }
              continue;
            }
            if (event.event !== 'tokens') throw new Error(`Unknown token stream event: ${event.event}`);
            const snapshot = NativeTokenSnapshotSchema.parse(value);
            if (snapshot.sessionId !== sessionId) throw new Error('Token usage belongs to another work chat.');
            if (!valid()) return;
            const previous = state.nativeTokens.data;
            if (previous?.epoch === snapshot.epoch && previous.sequence > snapshot.sequence) continue;
            receivedAt ??= Date.now();
            publish({nativeTokens: ready(snapshot)});
          }
        }
      } catch (error) {
        interrupted = error;
      } finally {
        if (deadline) clearTimeout(deadline);
        controller.signal.removeEventListener('abort', stopAttempt);
        attempt.abort();
        reader?.releaseLock();
      }
      if (!valid()) return;
      rejectExpiredAccount(path, interrupted, authenticated, accountGeneration);
      if (!valid()) return;
      if (!(interrupted instanceof TokenTransportError)) throw interrupted;
      // A fresh initial snapshot alone does not reset a flapping connection's budget.
      if (receivedAt !== null && Date.now() - receivedAt >= 30_000) failures = 0;
      if (failures >= 5) throw interrupted;
      publish({nativeTokens: {status: 'loading', data: null, error: plannedRestart ? null : errorDetails(interrupted)}});
      const delay = Math.min(15_000, 1000 * 2 ** failures++) * (0.75 + Math.random() * 0.5);
      await abortableDelay(delay, controller.signal);
      await waitForTokenReconnect(controller.signal);
    }
  })().catch(error => {
    if (valid()) publish({nativeTokens: {status: 'error', data: null, error: errorDetails(error)}});
  });
}
async function waitForTokenReconnect(signal: AbortSignal): Promise<void> {
  if (signal.aborted || (document.visibilityState === 'visible' && navigator.onLine)) return;
  await new Promise<void>(resolve => {
    const finish = () => {
      if (!signal.aborted && (document.visibilityState !== 'visible' || !navigator.onLine)) return;
      document.removeEventListener('visibilitychange', finish);
      window.removeEventListener('online', finish);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    document.addEventListener('visibilitychange', finish);
    window.addEventListener('online', finish);
    signal.addEventListener('abort', finish, {once: true});
    finish();
  });
}
function reconnectNativeTokens(): void {
  session();
  const sessionId = state.nativeTarget?.sessionId;
  if (!sessionId || state.nativeConnection.status !== 'open') throw new Error('Connect to the work chat before reading its tokens.');
  streamNativeTokens(sessionId, nativeGeneration);
}
async function openHermes(sessionId: string | null = null): Promise<NativeSessionMapping | null> {
  session();
  if (sessionId !== null && state.nativeDetail.data?.session.sessionId === sessionId && state.nativeConnection.status === 'open') {
    return state.nativeDetail.data.session;
  }
  const selectedAgentId = state.nativeTarget?.sessionId === sessionId ? state.nativeSelectedAgentId : null;
  closeHermes();
  const generation = nativeGeneration;
  const accountGeneration = authGeneration;
  publish({...nativeResources(), nativeTarget: {sessionId}, nativeSelectedAgentId: null,
    nativeConnection: {status: 'connecting', error: null}});
  try {
    const target = sessionId === null
      ? (await request<NativeSessionMapping>('/hermes/sessions', {body: {mode: 'ensure'}, csrf: session().csrfToken})).sessionId
      : sessionId;
    if (generation !== nativeGeneration || accountGeneration !== authGeneration) return null;
    const detail = await refreshNativeDetail(target, generation);
    if (generation !== nativeGeneration || detail === null) return null;
    // Reading an existing chat can start its runtime. Read message display settings
    // after that start, before exposing a connected conversation.
    await refreshStartedWorkspace(accountGeneration, generation);
    if (generation !== nativeGeneration || accountGeneration !== authGeneration) return null;
    if (state.accountSettings.status !== 'ready' || !state.accountSettings.data?.messagePrefix) {
      throw new Error('The running team did not return its message display settings.');
    }
    if (selectedAgentId && detail.agents.some(agent => agent.sessionId === selectedAgentId)) {
      // The selected transcript keeps its own error surface during reconnection.
      await selectNativeAgent(selectedAgentId).catch(() => undefined);
      if (generation !== nativeGeneration) return null;
    }
    publish({nativeTarget: {sessionId: target}, nativeConnection: {status: 'open', error: null}});
    pollHermes(target, generation);
    streamNativeTokens(target, generation);
    // Reading the list has its own error surface and request generation.
    void refreshNativeSessions().catch(() => undefined);
    return detail.session;
  } catch (error) {
    if (generation === nativeGeneration) publish({nativeConnection: {status: 'error', error: errorDetails(error)}});
    throw error;
  }
}
async function selectNativeAgent(agentId: string | null): Promise<void> {
  const mapping = state.nativeDetail.data?.session;
  if (!mapping) throw new Error('Open your Hermes conversation first.');
  if (state.nativeSelectedAgentId === agentId) return;
  nativeAgentGeneration++;
  publish({nativeSelectedAgentId: agentId, nativeAgent: idle<NativeAgentDetail>(), nativeMessageResult: idle<NativeMessageResult>()});
  if (agentId) {
    const generation = nativeGeneration;
    const agentGeneration = nativeAgentGeneration;
    await query('nativeAgent', () => readNativeAgent(mapping.sessionId, agentId),
      () => generation === nativeGeneration && agentGeneration === nativeAgentGeneration && state.nativeSelectedAgentId === agentId);
  }
}
async function sendNativeMessage(text: string): Promise<NativeMessageResult> {
  const detail = state.nativeDetail.data;
  if (!detail || state.nativeConnection.status !== 'open' || detail.session.sessionId !== state.nativeTarget?.sessionId) {
    throw new Error('Connect to your work chat before sending a message.');
  }
  const generation = nativeGeneration;
  const agentId = state.nativeSelectedAgentId;
  const execution = agentId === null ? detail.state : detail.agents.find(agent => agent.sessionId === agentId);
  if (!execution) throw new Error('The selected agent is not present in this work chat.');
  if (execution.resuming && !execution.running) throw new Error('The selected agent is resuming. Wait before sending another message.');
  const path = `/hermes/sessions/${encodeURIComponent(detail.session.sessionId)}` +
    (agentId === null ? '' : `/agents/${encodeURIComponent(agentId)}`);
  const result = await query('nativeMessageResult', () => request<NativeMessageResult>(`${path}/messages`, {body: {text}, csrf: session().csrfToken}),
    () => generation === nativeGeneration && state.nativeSelectedAgentId === agentId);
  if (generation === nativeGeneration) refreshAfterNativeAction(detail.session.sessionId, generation);
  return result;
}
function refreshAfterNativeAction(sessionId: string, generation: number): void {
  // A failed read must not make an accepted message or interrupt look unsent.
  void refreshNativeDetail(sessionId, generation).catch(error => {
    if (generation === nativeGeneration) publish({nativeConnection: {status: 'error', error: errorDetails(error)}});
  });
}

export const hermesActions = {
  load(): Promise<void> {
    if (initialLoad) return initialLoad;
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const returnedGoogleError = fragment.get(GOOGLE_ERROR_FRAGMENT);
    if (returnedGoogleError !== null) {
      fragment.delete(GOOGLE_ERROR_FRAGMENT);
      const remaining = fragment.toString();
      window.history.replaceState(window.history.state, '',
        `${window.location.pathname}${window.location.search}${remaining ? `#${remaining}` : ''}`);
      try {
        const {stack, errors, ...details} = GoogleCallbackErrorSchema.parse(JSON.parse(returnedGoogleError));
        const cause = errors === undefined ? details.cause : {cause: details.cause, errors};
        publish({operation: {label: null, scope: 'login', error: {...details,
          ...(stack === undefined ? {} : {stack}), ...(cause === undefined ? {} : {cause})}}});
      }
      catch (error) {publish({operation: {label: null, scope: 'login', error: errorDetails(error)}});}
    }
    initialLoad = (async () => {
      await Promise.allSettled([query('config', loadConfig), query('health', () => request<Health>('/health', {health: true})), refreshSession()]);
      if (state.session.data) await refreshAccount();
      window.addEventListener('focus',refreshAccountOnFocus);
      document.addEventListener('visibilitychange',refreshAccountOnFocus);
    })();
    return initialLoad;
  },
  async refresh(): Promise<void> {
    await Promise.allSettled([query('config', loadConfig), query('health', () => request<Health>('/health', {health: true})), refreshSession()]);
    if (state.session.data) await refreshAccount();
  },
  connectWallet: () => operation('Connect wallet', async () => {
    const wallet = await import('./wallet');
    const address = await wallet.connectWallet(config()); publish({walletAddress: address}); return address;
  }),
  addWalletNetwork: () => operation('Add wallet network', async () => (await import('./wallet')).addWalletNetwork(config())),
  signIn: (email: string, password: string) => operation('Signing in…', () => authenticateWithPassword('/auth/login', email, password), 'login'),
  register: (email: string, password: string) => operation('Creating your account…', () => authenticateWithPassword('/auth/register', email, password), 'register'),
  setupLogin: (token: string, email: string, password: string) => operation('Saving sign-in details…', () => authenticateWithPassword('/auth/setup-login', email, password, token), 'setup-login'),
  signInWithWallet: () => operation('Complete sign-in in your wallet.', async () => {
    const wallet = await import('./wallet');
    const configured = config();
    const address = await wallet.connectWallet(configured); publish({walletAddress: address});
    stopWalletWatch?.(); stopWalletWatch = wallet.watchWallet(accounts => publish({walletAddress: accounts[0] ?? null}));
    const challenge = await request<{challengeId: string; message: string; expiresAt: string}>('/auth/challenge', {body: {address}});
    const signature = await wallet.signWalletMessage(configured, address, challenge.message);
    const authenticated = await request<Session>('/auth/verify', {body: {challengeId: challenge.challengeId, signature}});
    acceptSession(authenticated); await refreshAccount(); return authenticated;
  }, 'login'),
  signOut: () => operation('Sign out', async () => {
    await request<void>('/auth/logout', {body: {}, csrf: session().csrfToken});
    acceptSession(null);
  }),
  openHermes, closeHermes, selectNativeAgent, refreshHermesRuntime, reconnectNativeTokens,
  refreshNativeSessions,
  createNativeChat: (title?: string) => operation('Create work chat', async () => {
    const generation = authGeneration;
    const created = await request<NativeSessionMapping>('/hermes/sessions',
      {body: {mode: 'create', ...(title === undefined ? {} : {title})}, csrf: session().csrfToken});
    if (generation !== authGeneration) throw new Error('The account changed before the work chat was created.');
    // A failed list read must not turn a completed create into a second create.
    void refreshNativeSessions().catch(() => undefined);
    return created;
  }),
  openNativeTerminal: async (sessionId: string, agentId: string | null, attachId: string, signal: AbortSignal): Promise<NativeTerminalTicket> => {
    const authenticated = session();
    const generation = authGeneration;
    const ticket = await request<NativeTerminalTicket>(`/hermes/sessions/${encodeURIComponent(sessionId)}/terminal`,
      {body: {...(agentId === null ? {} : {agentId}), attachId}, csrf: authenticated.csrfToken, signal});
    if (generation !== authGeneration) throw new Error('The account changed before the terminal opened.');
    return ticket;
  },
  refreshHermes: async (sessionId: string | null = state.nativeTarget?.sessionId ?? null) => {
    session();
    const detail = state.nativeDetail.data;
    if (state.nativeConnection.status !== 'open' || !detail ||
        (sessionId !== null && detail.session.sessionId !== sessionId)) return openHermes(sessionId);
    const generation = nativeGeneration;
    const accountGeneration = authGeneration;
    try {
      await refreshStartedWorkspace(accountGeneration, generation);
      if (generation !== nativeGeneration || accountGeneration !== authGeneration) return null;
      const refreshed = await refreshNativeDetail(detail.session.sessionId, generation);
      return refreshed?.session ?? null;
    }
    catch (error) {
      if (generation === nativeGeneration) publish({nativeConnection: {status: 'error', error: errorDetails(error)}});
      throw error;
    }
  },
  configureHermes: () => operation(PRODUCT_ENTRY_LABEL, async () => {
    const generation = authGeneration;
    const runtime = await request<NativeRuntimeStatus>('/hermes/runtime', {body: {}, csrf: session().csrfToken});
    if (generation !== authGeneration) throw new Error('The account changed before Hermes started.');
    nativeRuntimeGeneration++;
    publish({nativeRuntime: ready(runtime)});
    await Promise.allSettled([refreshAccountSettings()]);
    return runtime;
  }),
  sendNativeMessage: (text: string) => operation('Send message', () => sendNativeMessage(text)),
  answerNativeRequest: (requestId: string, result: Record<string, unknown>) => operation('Send answer', async () => {
    const detail = state.nativeDetail.data;
    if (!detail || state.nativeConnection.status !== 'open') throw new Error('Connect to your work chat before answering.');
    if (!detail.openRequests.some(item => item.id === requestId)) throw new Error('This request is no longer open in the workspace snapshot.');
    const generation = nativeGeneration;
    const response = await request<unknown>(`/hermes/sessions/${encodeURIComponent(detail.session.sessionId)}/requests/${encodeURIComponent(requestId)}`,
      {body: {result}, csrf: session().csrfToken});
    if (generation === nativeGeneration) refreshAfterNativeAction(detail.session.sessionId, generation);
    return response;
  }),
  interruptNativeAgent: () => operation('Stop agent', async () => {
    const sessionId = state.nativeDetail.data?.session.sessionId;
    const agentId = state.nativeSelectedAgentId;
    if (!sessionId || !agentId) throw new Error('Select a working subagent first.');
    const generation = nativeGeneration;
    const result = await request<NativeMessageResult>(`/hermes/sessions/${encodeURIComponent(sessionId)}/agents/${encodeURIComponent(agentId)}/interrupt`,
      {body: {}, csrf: session().csrfToken});
    if (generation === nativeGeneration) {publish({nativeMessageResult: ready(result)}); refreshAfterNativeAction(sessionId, generation);}
    return result;
  }),
  refreshAccountSettings, refreshAccountProfile, refreshStorage: refreshHermesRuntime,
  refreshApplications, readApplicationLogs,
  registerApplication: (definition: ApplicationRegistration) => operation('Add service', () => changeApplication('/account/applications', definition)),
  startApplication: (slug: string) => operation('Start service', () => changeApplication(`/account/applications/${encodeURIComponent(slug)}/start`, {})),
  stopApplication: (slug: string) => operation('Stop service', () => changeApplication(`/account/applications/${encodeURIComponent(slug)}/stop`, {})),
  closeApplicationLogs(): void {
    applicationLogsGeneration++;
    publish({applicationLogSlug: null, applicationLogs: idle<ApplicationLogs>()});
  },
  saveAccountUsername: (username: string, expectedUsername: string) => operation('Save username', () => changeAccountUsername(username, expectedUsername)),
  refreshWhatsApp,
  refreshGoogle,
  connectGoogle: (services: GoogleService[]) => operation('Connect Google', () => authorizeGoogle('/account/google/connect', {services})),
  linkGoogleSignIn: () => operation('Enable Google sign-in', () => authorizeGoogle('/account/google/link-sign-in', {})),
  signInGoogle: (returnPath: string, mode: AuthMode) => operation('Continue with Google', () => authorizeGoogle('/auth/google/start', {returnPath}), mode),
  disconnectGoogle: () => operation('Disconnect Google', async () => {
    const generation = authGeneration;
    const status = GoogleStatusSchema.parse(await request('/account/google', {method: 'DELETE', body: {}, csrf: session().csrfToken}));
    if (generation !== authGeneration) throw new Error('The account changed before Google was disconnected.');
    googleGeneration++;
    publish({google: ready(status)});
    return status;
  }),
  pairWhatsApp: (sessionId?: string) => operation('Connect WhatsApp', async () => {
    const generation = authGeneration;
    const status = await changeWhatsApp('/account/whatsapp/pair', 'POST', sessionId === undefined ? {} : {sessionId});
    await refreshStartedWorkspace(generation);
    if (generation !== authGeneration) throw new Error('The account changed before the workspace state was refreshed.');
    return status;
  }),
  disconnectWhatsApp: () => operation('Disconnect WhatsApp', () => changeWhatsApp('/account/whatsapp', 'DELETE')),
  saveMessagePrefix: (enabled: boolean) => operation('Save message prefix', () =>
    changeAccountSettings('/account/message-prefix', 'PUT', {enabled})),
  saveModelKey: (apiKey: string, provider: import('../../src/model-catalog').ModelProvider) => operation('Save API key', async () => {
    const saved = await changeAccountSettings('/account/model-key', 'PUT', {apiKey, provider});
    closeHermes(); publish({...nativeResources()});
    await refreshHermesRuntime(); return saved;
  }),
  deleteModelKey: (provider: import('../../src/model-catalog').ModelProvider) => operation('Remove API key', async () => {
    const saved = await changeAccountSettings('/account/model-key', 'DELETE', {provider});
    closeHermes(); publish({...nativeResources()});
    await refreshHermesRuntime(); return saved;
  }),
  saveAccountDocument: (name: string, content: string, expectedRevision: AccountDocument['revision'] | null) => operation('Save document', async () => {
    const settings = await changeAccountSettings(`/account/documents/${encodeURIComponent(name)}`, 'PUT', {content, expectedRevision});
    const document = settings.documents?.find(item => item.name === name);
    if (!document) throw new Error('The service did not return the saved document.');
    await Promise.allSettled([refreshHermesRuntime()]);
    return document;
  }),
  deleteAccountDocument: (name: string, expectedRevision: AccountDocument['revision']) => operation('Delete document', () =>
    changeAccountSettings(`/account/documents/${encodeURIComponent(name)}`, 'DELETE', {expectedRevision})),
  clearError(): void {publish({operation: {...state.operation, error: null}});}
};

export function useHermes() {
  const snapshot = useSyncExternalStore(callback => {listeners.add(callback); return () => listeners.delete(callback);}, () => state);
  return {state: snapshot, actions: hermesActions};
}
